import { useCallback, useMemo } from 'react';
import {
  ReactFlow,
  Background,
  BackgroundVariant,
  Handle,
  Position,
  type Node,
  type Edge,
  type NodeProps,
} from '@xyflow/react';
import '@xyflow/react/dist/style.css';
import {
  Workflow, Shield, GitBranch, Database, Code2, FileJson,
  Play, Globe, Inbox, Save, CheckCircle2,
  Search, ChevronDown, ArrowRightLeft,
  FileSpreadsheet, Map, Repeat, MessageSquare, AlertTriangle,
  type LucideIcon,
} from 'lucide-react';
import { useStepSequencer, type SequencerStep } from './useStepSequencer';
import {
  PIPELINE_NODES,
  PIPELINE_EDGES,
  PIPELINE_NODE_ORDER,
  PALETTE_CATEGORIES,
} from './demo-data';
import { cn } from '@/lib/utils';

/* ------------------------------------------------------------------ */
/*  Animation Steps                                                    */
/* ------------------------------------------------------------------ */

const PIPELINE_STEPS: SequencerStep[] = [
  { id: 'build-0', duration: 600 },
  { id: 'build-1', duration: 600 },
  { id: 'build-2', duration: 600 },
  { id: 'build-3', duration: 600 },
  { id: 'build-4', duration: 600 },
  { id: 'build-5', duration: 600 },
  { id: 'build-6', duration: 600 },
  { id: 'build-7', duration: 500 },
  { id: 'build-8', duration: 500 },
  { id: 'running', duration: 2500 },
  { id: 'success', duration: 4000 },
];

/* ------------------------------------------------------------------ */
/*  Node Config (matches PipelineCustomNode.tsx exactly)                    */
/* ------------------------------------------------------------------ */

interface PipelineNodeConfig {
  icon: LucideIcon;
  bg: string;
  border: string;
  iconColor: string;
  typeLabel: string;
}

const nodeConfig: Record<string, PipelineNodeConfig> = {
  start: { icon: Play, bg: 'bg-emerald-500/20', border: 'border-emerald-500/50', iconColor: 'text-emerald-400', typeLabel: 'TRIGGER' },
  httpRequest: { icon: Globe, bg: 'bg-blue-500/10', border: 'border-blue-500/30', iconColor: 'text-blue-400', typeLabel: 'EXTRACT' },
  json: { icon: FileJson, bg: 'bg-blue-500/10', border: 'border-blue-500/30', iconColor: 'text-blue-400', typeLabel: 'TRANSFORM' },
  redactMask: { icon: Shield, bg: 'bg-rose-500/10', border: 'border-rose-500/30', iconColor: 'text-rose-400', typeLabel: 'TRANSFORM' },
  condition: { icon: GitBranch, bg: 'bg-amber-500/10', border: 'border-amber-500/30', iconColor: 'text-amber-400', typeLabel: 'FLOW' },
  lookup: { icon: Database, bg: 'bg-cyan-500/10', border: 'border-cyan-500/30', iconColor: 'text-cyan-400', typeLabel: 'TRANSFORM' },
  transform: { icon: Code2, bg: 'bg-violet-500/10', border: 'border-violet-500/30', iconColor: 'text-violet-400', typeLabel: 'TRANSFORM' },
  logpulseIngest: { icon: Inbox, bg: 'bg-cyan-500/10', border: 'border-cyan-500/30', iconColor: 'text-cyan-400', typeLabel: 'LOAD' },
};

/* ------------------------------------------------------------------ */
/*  Palette icons — maps iconKey from demo-data to actual Lucide icon  */
/* ------------------------------------------------------------------ */

const CATEGORY_ICONS: Record<string, LucideIcon> = {
  trigger: Play,
  extract: Globe,
  transform: Code2,
  flowControl: GitBranch,
  load: Inbox,
  utility: MessageSquare,
};

const ITEM_ICONS: Record<string, LucideIcon> = {
  arrowRightLeft: ArrowRightLeft,
  fileJson: FileJson,
  fileSpreadsheet: FileSpreadsheet,
  shield: Shield,
  map: Map,
  database: Database,
  gitBranch: GitBranch,
  repeat: Repeat,
  messageSquare: MessageSquare,
  alertTriangle: AlertTriangle,
  search: Search,
};

/* ------------------------------------------------------------------ */
/*  Custom Node (matches PipelineCustomNode)                                */
/* ------------------------------------------------------------------ */

function PipelineMockNode({ data }: NodeProps) {
  const d = data as Record<string, string>;
  const config = nodeConfig[d.nodeType] || nodeConfig.transform;
  const Icon = config.icon;
  const isCondition = d.nodeType === 'condition';
  const isStart = d.nodeType === 'start';
  const status = d.status as string | undefined;

  const isHidden = status === 'hidden';

  return (
    <div className={cn(
      'rounded-xl border-2 min-w-[150px] bg-gray-950 transition-all duration-500',
      isHidden ? 'border-transparent bg-transparent opacity-0' : config.border,
      status === 'running' && 'ring-2 ring-blue-500 ring-offset-2 ring-offset-gray-950 animate-pulse',
      status === 'success' && 'ring-2 ring-emerald-500 ring-offset-2 ring-offset-gray-950',
    )}>
      {/* Status dot */}
      {status === 'success' && (
        <div className="absolute -top-1 -right-1 w-3 h-3 rounded-full bg-emerald-500 border-2 border-gray-950" />
      )}

      {/* Input handle */}
      {!isStart && (
        <Handle
          type="target"
          position={Position.Left}
          className="!w-3 !h-3 !border-2 !bg-gray-800 !border-gray-500"
        />
      )}

      {/* Header */}
      <div className={cn('px-3 py-1.5 border-b border-gray-800/50 flex items-center gap-2', config.bg)}>
        <Icon className={cn('h-3.5 w-3.5', config.iconColor)} />
        <span className="text-[10px] font-semibold uppercase tracking-wide text-gray-400">
          {config.typeLabel}
        </span>
      </div>

      {/* Body */}
      <div className={cn('px-3 py-2.5', isCondition ? 'min-h-[72px]' : 'min-h-[40px]')}>
        <span className="text-xs font-medium text-gray-100">{d.label}</span>

        {isCondition && (
          <div className="mt-2 space-y-1">
            <div className="flex items-center gap-1.5">
              <div className="w-2 h-2 rounded-full" style={{ backgroundColor: '#22c55e' }} />
              <span className="text-[10px] text-gray-400">level = error</span>
            </div>
            <div className="flex items-center gap-1.5">
              <div className="w-2 h-2 rounded-full" style={{ backgroundColor: '#71717a' }} />
              <span className="text-[10px] text-gray-400">else</span>
            </div>
          </div>
        )}
      </div>

      {/* Output handles */}
      {isCondition ? (
        <>
          <Handle type="source" position={Position.Right} id="true"
            className="!w-3 !h-3 !border-2 !bg-emerald-400/50 !border-emerald-400"
            style={{ top: '45%' }}
          />
          <Handle type="source" position={Position.Right} id="else"
            className="!w-3 !h-3 !border-2 !bg-gray-500/50 !border-gray-500"
            style={{ top: '75%' }}
          />
        </>
      ) : (
        <Handle
          type="source"
          position={Position.Right}
          className="!w-3 !h-3 !border-2 !bg-gray-800 !border-gray-500"
        />
      )}
    </div>
  );
}

const nodeTypes = { pipelineNode: PipelineMockNode };

/* ------------------------------------------------------------------ */
/*  Main PipelineDemo Component                                        */
/* ------------------------------------------------------------------ */

interface PipelineDemoProps {
  enabled?: boolean;
}

export function PipelineDemo({ enabled = true }: PipelineDemoProps) {
  const { currentStepId } = useStepSequencer({
    steps: PIPELINE_STEPS,
    enabled,
    loop: true,
  });

  const noop = useCallback(() => {}, []);

  // Determine how many nodes are visible based on current step
  const visibleCount = useMemo(() => {
    if (currentStepId.startsWith('build-')) {
      return parseInt(currentStepId.split('-')[1], 10) + 1;
    }
    return PIPELINE_NODE_ORDER.length; // all visible for running/success
  }, [currentStepId]);

  const isRunning = currentStepId === 'running';
  const isSuccess = currentStepId === 'success';

  // Always include ALL nodes for correct fitView, but control visibility via data.hidden/status
  const animatedNodes: Node[] = useMemo(() => {
    const visibleIds = new Set(PIPELINE_NODE_ORDER.slice(0, visibleCount));

    return PIPELINE_NODES.map((n) => {
      const isVisible = visibleIds.has(n.id);
      let status: string | undefined;
      if (!isVisible) status = 'hidden';
      else if (isRunning) status = 'running';
      else if (isSuccess) status = 'success';
      return {
        ...n,
        data: { ...n.data, status },
      };
    });
  }, [visibleCount, isRunning, isSuccess]);

  // Show edges only when both source and target are visible
  const animatedEdges: Edge[] = useMemo(() => {
    const visibleIds = new Set(PIPELINE_NODE_ORDER.slice(0, visibleCount));
    return PIPELINE_EDGES.map((e) => ({
      ...e,
      hidden: !(visibleIds.has(e.source) && visibleIds.has(e.target)),
    }));
  }, [visibleCount]);

  return (
    <div className="rounded-2xl border border-gray-200/60 dark:border-gray-700/40 bg-white dark:bg-gray-950 shadow-2xl overflow-hidden max-w-full">
      {/* Browser chrome */}
      <div className="flex items-center gap-2 px-4 py-2.5 border-b border-gray-200/60 dark:border-gray-800 bg-gray-50 dark:bg-gray-900">
        <div className="flex gap-1.5">
          <div className="w-3 h-3 rounded-full bg-red-400/60" />
          <div className="w-3 h-3 rounded-full bg-amber-400/60" />
          <div className="w-3 h-3 rounded-full bg-emerald-400/60" />
        </div>
        <div className="flex-1 mx-6">
          <div className="h-5 rounded-md bg-gray-200/60 dark:bg-gray-800 max-w-xs mx-auto flex items-center justify-center">
            <span className="text-[10px] text-gray-500">app.logpulse.io/pipelines/prod-ingestion</span>
          </div>
        </div>
      </div>

      {/* Editor header */}
      <div className="flex items-center gap-2 sm:gap-3 px-2 sm:px-4 py-2 sm:py-2.5 border-b border-gray-200 dark:border-gray-800 bg-gray-50 dark:bg-gray-950 overflow-hidden">
        <div className="flex items-center gap-1.5 sm:gap-2 shrink-0">
          <Workflow className="h-3.5 w-3.5 sm:h-4 sm:w-4 text-primary-500" />
          <span className="text-xs sm:text-sm font-medium text-gray-900 dark:text-gray-100 truncate max-w-[120px] sm:max-w-none">Production Log Ingestion</span>
        </div>

        {/* Tab selector — hidden on mobile */}
        <div className="hidden sm:flex ml-4 items-center gap-0.5 bg-gray-200 dark:bg-gray-900 rounded-lg p-0.5">
          <span className="px-3 py-1 rounded-md text-xs bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 shadow-sm">Editor</span>
          <span className="px-3 py-1 rounded-md text-xs text-gray-500">Settings</span>
          <span className="px-3 py-1 rounded-md text-xs text-gray-500">Runs</span>
        </div>

        <div className="ml-auto flex items-center gap-1.5 sm:gap-2 shrink-0">
          {/* Save button */}
          <button className="flex items-center gap-1 sm:gap-1.5 h-6 sm:h-7 px-2 sm:px-3 rounded-md text-[10px] sm:text-xs font-medium bg-gray-200 dark:bg-gray-800 text-gray-700 dark:text-gray-300">
            {isSuccess ? (
              <CheckCircle2 className="h-3 w-3 sm:h-3.5 sm:w-3.5 text-emerald-500" />
            ) : (
              <Save className="h-3 w-3 sm:h-3.5 sm:w-3.5" />
            )}
            <span className="hidden sm:inline">Saved</span>
          </button>
          {/* Run button */}
          <button className={cn(
            'flex items-center gap-1 sm:gap-1.5 h-6 sm:h-7 px-2 sm:px-3 rounded-md text-[10px] sm:text-xs font-medium',
            isRunning ? 'bg-blue-600 text-white' : 'bg-blue-600 text-white hover:bg-blue-500'
          )}>
            <Play className="h-3 w-3 sm:h-3.5 sm:w-3.5" />
            <span>{isRunning ? 'Running...' : 'Run'}</span>
          </button>
          {/* Status */}
          <span className={cn(
            'hidden sm:inline text-[10px] font-medium px-2 py-0.5 rounded-full border',
            isSuccess
              ? 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20'
              : isRunning
                ? 'bg-blue-500/10 text-blue-400 border-blue-500/20'
                : 'bg-gray-800 text-gray-500 border-gray-700'
          )}>
            {isSuccess ? 'Last run: Success' : isRunning ? 'Running' : 'Active'}
          </span>
        </div>
      </div>

      {/* Editor body */}
      <div className="flex h-[280px] sm:h-[360px]">
        {/* Node palette (hidden on small screens) */}
        <div className="hidden lg:flex flex-col w-44 bg-gray-950 border-r border-gray-800 overflow-hidden">
          <div className="p-3 border-b border-gray-800">
            <span className="text-xs font-semibold text-gray-100">Nodes</span>
            <div className="mt-2 relative">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-gray-500" />
              <div className="w-full pl-8 pr-3 py-1.5 text-xs bg-gray-900 border border-gray-800 rounded-lg text-gray-500">
                Search nodes...
              </div>
            </div>
          </div>
          <div className="flex-1 overflow-y-auto p-2 space-y-2">
            {PALETTE_CATEGORIES.map((cat) => (
                <div key={cat.key}>
                  <button className="flex items-center gap-1 w-full px-2 py-1.5">
                    <ChevronDown className="w-3 h-3 text-gray-400" />
                    <span className="text-[10px] font-semibold uppercase tracking-wide text-gray-400">
                      {cat.label}
                    </span>
                    <span className="ml-auto text-[10px] text-gray-600">{cat.items.length}</span>
                  </button>
                  <div className="space-y-1 mt-0.5">
                    {cat.items.map((item) => {
                      const ItemIcon = (item.iconKey && ITEM_ICONS[item.iconKey]) || CATEGORY_ICONS[cat.key] || Code2;
                      return (
                        <div
                          key={item.label}
                          className="flex items-center gap-2.5 px-2.5 py-1.5 rounded-lg bg-gray-900 border border-gray-800 cursor-grab"
                        >
                          <div className={cn(
                            'w-7 h-7 rounded-lg flex items-center justify-center shrink-0',
                            `bg-${cat.color}-500/10`
                          )}>
                            <ItemIcon className={cn('w-3.5 h-3.5', item.iconColor)} />
                          </div>
                          <div className="min-w-0">
                            <span className="text-xs font-medium text-gray-100 truncate block">{item.label}</span>
                            <span className="text-[10px] text-gray-500 truncate block">{item.description}</span>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              ))}
          </div>
        </div>

        {/* ReactFlow canvas */}
        <div className="flex-1 bg-gray-950">
          <ReactFlow
            nodes={animatedNodes}
            edges={animatedEdges}
            nodeTypes={nodeTypes}
            fitView
            fitViewOptions={{ maxZoom: 0.65, padding: 0.08 }}
            minZoom={0.15}
            nodesDraggable={false}
            nodesConnectable={false}
            elementsSelectable={false}
            panOnDrag={false}
            zoomOnScroll={false}
            zoomOnPinch={false}
            zoomOnDoubleClick={false}
            preventScrolling={false}
            onNodeClick={noop}
            proOptions={{ hideAttribution: true }}
          >
            <Background variant={BackgroundVariant.Dots} gap={20} size={1} color="#1D2939" />
          </ReactFlow>
        </div>
      </div>
    </div>
  );
}
